// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

/**
 * Usage tracker — records what vets LOOK AT (vic/vetpo#49) into ada-core
 * `usage-events` via `POST /api/usage-events/batch`.
 *
 * WHAT GOES OVER THE WIRE
 * Only `{type, section?, detail?, pet?, at}` per event. Every value except
 * `pet` (an id) and `at` (a timestamp) is a literal from the closed vocabulary
 * below, and the server rejects the whole batch on any other key. There is no
 * place for a glucose value, a note, or a name — by construction, not by
 * discipline. Who the vet is, their clinic, and whether the account is
 * internal/synthetic are decided SERVER-side from the session (same rule as
 * recordExportEvent: a client can't be trusted to label its own traffic).
 *
 * ⚠️ FIRE-AND-FORGET, never in the way.
 * `track()` only pushes onto an in-memory queue and can't throw. The queue
 * flushes every FLUSH_INTERVAL_MS, when the tab is hidden, and on pagehide
 * (fetch `keepalive` so the last batch survives a closed tab). A failed flush
 * is DROPPED, not retried: this is analytics, and a retry loop against a
 * struggling backend is exactly the chattiness the ticket forbids. A 403/404
 * disables the tracker for the rest of the page's life, so a build shipped
 * before ada-core's endpoint (or a role the endpoint refuses) makes one
 * request, not one every 30s.
 *
 * `trackNow()` is `track()` plus an immediate `flush()`, for events that are
 * sparse and high-value enough that they shouldn't wait out FLUSH_INTERVAL_MS
 * or depend on a `pagehide` that a fast tab close may not deliver (vic/vetpo#53:
 * a beta smoke run that opened reports and switched chart ranges within the
 * last ~30s of the session wrote zero `report_opened`/`report_exported`/
 * `chart_view` rows). Use it at the call site of a one-off action — a report
 * export, a chart-range switch — not for anything that fires in a burst.
 *
 * Auth: authHeaders() (Bearer from sessionStorage when present) plus
 * `credentials: 'include'`, so a cookie-only tab (VP-AUTH) is recorded too.
 */

export type UsageEventType =
  | 'page_view'
  | 'patient_view'
  | 'chart_view'
  | 'report_opened'
  | 'report_exported'
  | 'alert_list_opened'

export type UsageSection =
  | 'dashboard'
  | 'patient_detail'
  | 'patients'
  | 'clients'
  | 'register_client'
  | 'notes'
  | 'pet_shares'
  | 'clinic_team'
  | 'care_teams'
  | 'alerts'
  | 'report_dialog'
  | 'devices'
  | 'help'
  | 'account'
  | 'admin'
  | 'other'

export type UsageDetail = 'day' | 'week' | 'month' | 'all' | 'agp' | 'pdf' | 'csv'

export interface UsageEvent {
  type: UsageEventType
  section?: UsageSection
  detail?: UsageDetail
  pet?: number
}

interface QueuedEvent extends UsageEvent {
  at: string
}

const apiUrl = import.meta.env.VITE_BACKEND_API_URL || '/api'
export const USAGE_ENDPOINT = `${apiUrl}/usage-events/batch`

export const FLUSH_INTERVAL_MS = 30_000
/** Server cap per request (ada-core USAGE_BATCH_MAX). */
export const BATCH_MAX = 100
/** Memory bound if flushes keep failing: oldest events are dropped first. */
export const QUEUE_MAX = 500
/** The same event twice within this window is one view (double mounts, redirects). */
export const DEDUPE_WINDOW_MS = 10_000

let queue: QueuedEvent[] = []
let lastSeen = new Map<string, number>()
let disabled = false
let timer: ReturnType<typeof setInterval> | null = null
let listenersInstalled = false

const keyOf = (e: UsageEvent) => `${e.type}|${e.section ?? ''}|${e.detail ?? ''}|${e.pet ?? ''}`

/** Record one usage event. Never throws, never blocks. */
export function track(event: UsageEvent): void {
  try {
    if (disabled) return
    const now = Date.now()
    const key = keyOf(event)
    const prev = lastSeen.get(key)
    if (prev !== undefined && now - prev < DEDUPE_WINDOW_MS) return
    lastSeen.set(key, now)

    const pet = Number(event.pet)
    const queued: QueuedEvent = { type: event.type, at: new Date(now).toISOString() }
    if (event.section) queued.section = event.section
    if (event.detail) queued.detail = event.detail
    if (Number.isInteger(pet) && pet > 0) queued.pet = pet
    queue.push(queued)
    if (queue.length > QUEUE_MAX) queue = queue.slice(queue.length - QUEUE_MAX)
    ensureStarted()
  } catch {
    // Analytics must never surface to the vet.
  }
}

/** Record one usage event and flush it immediately, without waiting for the
 * interval timer or an unload event. See the header comment for when to use
 * this over plain `track()`. */
export function trackNow(event: UsageEvent): void {
  track(event)
  void flush()
}

/** Send everything queued. Resolves (never rejects) once the requests settle. */
export async function flush(opts: { keepalive?: boolean } = {}): Promise<void> {
  if (disabled || queue.length === 0) return
  const pending = queue
  queue = []
  for (let i = 0; i < pending.length; i += BATCH_MAX) {
    if (disabled) return
    const events = pending.slice(i, i + BATCH_MAX)
    try {
      const response = await deadlineFetch(USAGE_ENDPOINT, {
        method: 'POST',
        headers: authHeaders(),
        credentials: 'include',
        keepalive: opts.keepalive,
        body: JSON.stringify({ events }),
      })
      if (response.status === 403 || response.status === 404) {
        // Endpoint not deployed yet, or a role it refuses: stop for this page.
        disabled = true
        queue = []
        stop()
        return
      }
    } catch {
      // Dropped by design — see the header comment.
    }
  }
}

const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') void flush({ keepalive: true })
}
const onPageHide = () => {
  void flush({ keepalive: true })
}

function ensureStarted(): void {
  if (typeof window === 'undefined') return
  if (!timer) timer = setInterval(() => void flush(), FLUSH_INTERVAL_MS)
  if (!listenersInstalled) {
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)
    listenersInstalled = true
  }
}

function stop(): void {
  if (timer) clearInterval(timer)
  timer = null
  if (listenersInstalled && typeof window !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pagehide', onPageHide)
    listenersInstalled = false
  }
}

/**
 * The session is ending (sign-out). Send what was queued NOW, under this
 * session's credentials — flush() takes the queue and builds the request
 * headers synchronously, before the caller removes the token — then forget the
 * session's dedupe history and kill-switch. Sign-out does not reload the page,
 * so without this the queue was flushed after the NEXT vet signed in and
 * ada-core attributed these views to them (eng-brain P69-4). Fire-and-forget:
 * sign-out never waits on analytics.
 */
export function endUsageSession(): void {
  void flush({ keepalive: true })
  resetSessionState()
}

/**
 * A new session starts (sign-in). Anything still queued belongs to a session
 * that ended WITHOUT a sign-out (expired, signed out in another tab); its
 * credentials are gone, so it is dropped rather than sent as the new user.
 */
export function startUsageSession(): void {
  queue = []
  resetSessionState()
}

function resetSessionState(): void {
  lastSeen = new Map()
  disabled = false
}

/** Test-only: reset module state between tests. */
export function __resetUsageTrackerForTests(): void {
  stop()
  queue = []
  lastSeen = new Map()
  disabled = false
}

/** Test-only: inspect the queue without flushing. */
export function __peekUsageQueueForTests(): ReadonlyArray<QueuedEvent> {
  return queue
}

/**
 * Map a (hash-router) pathname to a portal section, plus a pet id for the
 * patient detail page. Unknown routes are 'other' rather than the raw path,
 * so nothing a URL might carry (a search term, an email in a filter) is sent.
 */
export function routeToUsageEvent(pathname: string): UsageEvent {
  const path = pathname.split('?')[0]
  const petShow = /^\/pets\/(\d+)\/show\b/.exec(path)
  if (petShow) return { type: 'patient_view', section: 'patient_detail', pet: Number(petShow[1]) }
  const first = path.split('/').filter(Boolean)[0] ?? ''
  const SECTIONS: Record<string, UsageSection> = {
    '': 'dashboard',
    pets: 'patients',
    owners: 'clients',
    'register-owner': 'register_client',
    emrs: 'notes',
    'vet-shares': 'pet_shares',
    'clinic-team': 'clinic_team',
    groups: 'care_teams',
    'group-members': 'care_teams',
    devices: 'devices',
    alerts: 'alerts',
    help: 'help',
    'whats-new': 'help',
    docs: 'help',
    consent: 'account',
    users: 'admin',
    leads: 'admin',
  }
  return { type: 'page_view', section: SECTIONS[first] ?? 'other' }
}
